import React, { useState, useEffect } from 'react';
import { Users, Plus, Search, Edit, Eye, CheckCircle, XCircle, ArrowUpDown, ArrowUp, ArrowDown, Filter, X, Home, Shield, Mail, Phone } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { useNotification } from '../../hooks/useNotification';
import { User } from '../../types/User';
import { Link } from 'react-router-dom';
import UsuarioModal from '../../components/UsuarioModal';

const Usuarios: React.FC = () => {
  const { userProfile } = useAuth();
  const { showSuccess, showError, showConfirm, NotificationComponent } = useNotification();
  const [usuarios, setUsuarios] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [cargoFilter, setCargoFilter] = useState('todos');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'nome' | 'cargo' | 'status'>('nome');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedUsuario, setSelectedUsuario] = useState<User | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showUsuarioModal, setShowUsuarioModal] = useState(false);

  // Carregar usuários do Firebase
  useEffect(() => {
    const loadUsuarios = async () => {
      if (!userProfile?.empresaCnpj) return;

      try {
        setLoading(true);
        setError(null);
        const data = await firebaseService.getUsuarios(userProfile.empresaCnpj);
        // Filtrar apenas usuários administrativos (não alunos, não professores) e excluir super user
        const usuariosAdmin = data.filter(u => {
          // Excluir super user pelo CPF
          if (u.cpf === '09052899606') return false;
          // Excluir professores (não têm acesso ao painel)
          if (u.cargo === 'professor') return false;
          // Filtrar apenas usuários administrativos
          return u.cargo && 
            ['administrador', 'admin', 'diretoria', 'financeiro'].includes(u.cargo);
        });
        setUsuarios(usuariosAdmin);
        console.log('✅ Usuários carregados:', usuariosAdmin.length);
      } catch (err) {
        console.error('❌ Erro ao carregar usuários:', err);
        setError('Erro ao carregar usuários');
      } finally {
        setLoading(false);
      }
    };

    loadUsuarios();
  }, [userProfile?.empresaCnpj]);

  // Função de ordenação
  const handleSort = (field: 'nome' | 'cargo' | 'status') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Filtrar e ordenar usuários
  const filteredAndSortedUsuarios = usuarios
    .filter(usuario => {
      const matchesSearch = (usuario.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (usuario.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (usuario.cargo || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'todos' || 
                           (statusFilter === 'ativo' && usuario.bAtivo) ||
                           (statusFilter === 'inativo' && !usuario.bAtivo);
      
      const matchesCargo = cargoFilter === 'todos' || usuario.cargo === cargoFilter;
      
      return matchesSearch && matchesStatus && matchesCargo;
    })
    .sort((a, b) => {
      let aValue: string | boolean;
      let bValue: string | boolean;

      switch (sortBy) {
        case 'nome':
          aValue = (a.nome || '').toLowerCase();
          bValue = (b.nome || '').toLowerCase();
          break;
        case 'cargo':
          aValue = (a.cargo || '').toLowerCase();
          bValue = (b.cargo || '').toLowerCase();
          break;
        case 'status':
          aValue = a.bAtivo;
          bValue = b.bAtivo;
          break;
        default:
          aValue = (a.nome || '').toLowerCase();
          bValue = (b.nome || '').toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  // Estatísticas
  const totalUsuarios = usuarios.length;
  const usuariosAtivos = usuarios.filter(u => u.bAtivo).length;
  const usuariosInativos = totalUsuarios - usuariosAtivos;
  const cargosUnicos = Array.from(new Set(usuarios.map(u => u.cargo).filter(Boolean)));

  // Funções de modal
  const handleViewUsuario = (usuario: User) => {
    setSelectedUsuario(usuario);
    setShowModal(true);
  };

  const handleEditUsuario = (usuario: User) => {
    setSelectedUsuario(usuario);
    setShowUsuarioModal(true);
  };

  const handleNewUsuario = () => {
    setSelectedUsuario(null);
    setShowUsuarioModal(true);
  };

  const handleCloseUsuarioModal = () => {
    setShowUsuarioModal(false);
    setSelectedUsuario(null);
  };

  const handleSaveUsuario = async (usuarioData: Partial<User> & { password?: string }) => {
    if (!userProfile?.empresaCnpj) return;

    try {
      if (usuarioData.id) {
        // Atualizar usuário existente
        await firebaseService.updateUsuario(userProfile.empresaCnpj, usuarioData.id, usuarioData);
      } else {
        // Criar novo usuário
        await firebaseService.createUsuario(userProfile.empresaCnpj, usuarioData);
      }
      
      // Recarregar dados
      const data = await firebaseService.getUsuarios(userProfile.empresaCnpj);
      const usuariosAdmin = data.filter(u => {
        // Excluir super user pelo CPF
        if (u.cpf === '09052899606') return false;
        // Excluir professores (não têm acesso ao painel)
        if (u.cargo === 'professor') return false;
        // Filtrar apenas usuários administrativos
        return u.cargo && 
          ['administrador', 'admin', 'diretoria', 'financeiro'].includes(u.cargo);
      });
      setUsuarios(usuariosAdmin);
      
      handleCloseUsuarioModal();
      showSuccess(
        usuarioData.id ? 'Usuário atualizado' : 'Usuário criado',
        usuarioData.id ? 'Os dados do usuário foram atualizados com sucesso!' : 'O usuário foi criado com sucesso!'
      );
    } catch (err: any) {
      console.error('❌ Erro ao salvar usuário:', err);
      showError('Erro ao salvar', err.message || 'Não foi possível salvar o usuário. Tente novamente.');
    }
  };

  const handleInativarUsuario = async (usuario: User) => {
    if (!userProfile?.empresaCnpj) return;

    showConfirm(
      usuario.bAtivo ? 'Inativar Usuário' : 'Ativar Usuário',
      `Tem certeza que deseja ${usuario.bAtivo ? 'inativar' : 'ativar'} o usuário ${usuario.nome}?`,
      async () => {
        try {
          if (usuario.bAtivo) {
            await firebaseService.inativarUsuario(userProfile.empresaCnpj, usuario.id);
          } else {
            await firebaseService.ativarUsuario(userProfile.empresaCnpj, usuario.id);
          }
          
          // Recarregar dados
          const data = await firebaseService.getUsuarios(userProfile.empresaCnpj);
          const usuariosAdmin = data.filter(u => 
            u.cargo && 
            ['administrador', 'admin', 'professor', 'diretoria', 'financeiro'].includes(u.cargo)
          );
          setUsuarios(usuariosAdmin);
          
          showSuccess(
            `Usuário ${usuario.bAtivo ? 'inativado' : 'ativado'}`,
            `O usuário foi ${usuario.bAtivo ? 'inativado' : 'ativado'} com sucesso!`
          );
        } catch (err: any) {
          console.error('❌ Erro ao alterar status do usuário:', err);
          showError('Erro ao alterar status', err.message || 'Não foi possível alterar o status do usuário. Tente novamente.');
        }
      }
    );
  };

  const getSortIcon = (field: 'nome' | 'cargo' | 'status') => {
    if (sortBy !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-primary-600" />
      : <ArrowDown className="h-4 w-4 text-primary-600" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <NotificationComponent />

      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-sm text-gray-500">
        <Link to="/" className="hover:text-gray-700">
          <Home className="h-4 w-4" />
        </Link>
        <span>/</span>
        <Link to="/configuracoes" className="hover:text-gray-700">Configurações</Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">Usuários</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Usuários Administrativos</h1>
          <p className="text-gray-600 mt-1">Gerencie usuários e seus acessos ao sistema</p>
        </div>
        <button
          onClick={handleNewUsuario}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Novo Usuário
        </button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total de Usuários</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalUsuarios}</p>
            </div>
            <div className="p-3 bg-primary-100 rounded-lg">
              <Users className="h-6 w-6 text-primary-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Usuários Ativos</p>
              <p className="text-2xl font-bold text-success-600 mt-1">{usuariosAtivos}</p>
            </div>
            <div className="p-3 bg-success-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-success-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Usuários Inativos</p>
              <p className="text-2xl font-bold text-gray-500 mt-1">{usuariosInativos}</p>
            </div>
            <div className="p-3 bg-gray-100 rounded-lg">
              <XCircle className="h-6 w-6 text-gray-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome, email ou cargo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>
          
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input"
            >
              <option value="todos">Todos os status</option>
              <option value="ativo">Ativos</option>
              <option value="inativo">Inativos</option>
            </select>

            <select
              value={cargoFilter}
              onChange={(e) => setCargoFilter(e.target.value)}
              className="input"
            >
              <option value="todos">Todos os cargos</option>
              {cargosUnicos.map(cargo => (
                <option key={cargo} value={cargo}>{cargo}</option>
              ))}
            </select>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('nome')}
                >
                  <div className="flex items-center gap-2">
                    Nome
                    {getSortIcon('nome')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('cargo')}
                >
                  <div className="flex items-center gap-2">
                    Cargo
                    {getSortIcon('cargo')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Telefone
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-2">
                    Status
                    {getSortIcon('status')}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAndSortedUsuarios.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    {searchTerm || statusFilter !== 'todos' || cargoFilter !== 'todos' 
                      ? 'Nenhum usuário encontrado com os filtros aplicados'
                      : 'Nenhum usuário cadastrado'}
                  </td>
                </tr>
              ) : (
                filteredAndSortedUsuarios.map((usuario) => (
                  <tr key={usuario.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                          {usuario.fotoUrl ? (
                            <img src={usuario.fotoUrl} alt={usuario.nome} className="h-10 w-10 rounded-full object-cover" />
                          ) : (
                            <Users className="h-5 w-5 text-primary-600" />
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{usuario.nome}</div>
                          {usuario.cpf && (
                            <div className="text-xs text-gray-500">CPF: {usuario.cpf}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Mail className="h-4 w-4 mr-2 text-gray-400" />
                        {usuario.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-800">
                        {usuario.cargo || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {usuario.telefone || usuario.cellular || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {usuario.bAtivo ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-800 flex items-center gap-1 w-fit">
                          <CheckCircle className="h-3 w-3" />
                          Ativo
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 flex items-center gap-1 w-fit">
                          <XCircle className="h-3 w-3" />
                          Inativo
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewUsuario(usuario)}
                          className="text-primary-600 hover:text-primary-900 p-1"
                          title="Visualizar"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleEditUsuario(usuario)}
                          className="text-gray-600 hover:text-gray-900 p-1"
                          title="Editar"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleInativarUsuario(usuario)}
                          className={`p-1 ${usuario.bAtivo ? 'text-danger-600 hover:text-danger-900' : 'text-success-600 hover:text-success-900'}`}
                          title={usuario.bAtivo ? 'Inativar' : 'Ativar'}
                        >
                          {usuario.bAtivo ? (
                            <XCircle className="h-5 w-5" />
                          ) : (
                            <CheckCircle className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Visualização */}
      {showModal && selectedUsuario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Detalhes do Usuário</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 rounded-full bg-primary-100 flex items-center justify-center">
                  {selectedUsuario.fotoUrl ? (
                    <img src={selectedUsuario.fotoUrl} alt={selectedUsuario.nome} className="h-20 w-20 rounded-full object-cover" />
                  ) : (
                    <Users className="h-10 w-10 text-primary-600" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedUsuario.nome}</h3>
                  <p className="text-sm text-gray-600">{selectedUsuario.email}</p>
                  <span className="inline-block mt-2 px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-800">
                    {selectedUsuario.cargo || 'N/A'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-sm font-medium text-gray-500">CPF</p>
                  <p className="text-sm text-gray-900">{selectedUsuario.cpf || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Telefone</p>
                  <p className="text-sm text-gray-900">{selectedUsuario.telefone || selectedUsuario.cellular || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <p className="text-sm text-gray-900">
                    {selectedUsuario.bAtivo ? (
                      <span className="text-success-600 font-medium">Ativo</span>
                    ) : (
                      <span className="text-gray-500 font-medium">Inativo</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Último Login</p>
                  <p className="text-sm text-gray-900">
                    {selectedUsuario.lastLogin 
                      ? new Date(selectedUsuario.lastLogin).toLocaleString('pt-BR')
                      : 'Nunca'}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowModal(false);
                  handleEditUsuario(selectedUsuario);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
              >
                Editar
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criar/Editar */}
      {showUsuarioModal && (
        <UsuarioModal
          usuario={selectedUsuario}
          onClose={handleCloseUsuarioModal}
          onSave={handleSaveUsuario}
        />
      )}
    </div>
  );
};

export default Usuarios;

